/**
 * @fileoverview Language Selector Component
 * This component provides a dropdown interface for selecting between different
 * languages. It includes support for the nine specified languages.
 * 
 * Features:
 * - Language selection via dropdown
 * - Haptic feedback integration
 * - Accessibility support
 * - Responsive design
 * 
 * @module components/chat/language-selector
 */
import { useState, useCallback } from 'react';
import { Check, Globe } from 'lucide-react';
import { useHapticFeedback } from '@/hooks/use-haptic';
import { useIsMobile } from '@/hooks/use-mobile';
import i18n from '@/lib/i18n';
import { Trans, useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { 
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { dynamicActivate, RTL_LOCALES } from '@/lib/i18n';
import { useLanguage } from '@/lib/language-context';
import { LocaleType } from '@/types/chat';
import { useTranslation as useAppTranslation } from '@/lib/translation-utils';
import { cn } from '@/lib/utils';
import ReactCountryFlag from 'react-country-flag';

/**
 * Props for the LanguageSelector component
 * @interface LanguageSelectorProps
 */
type LanguageSelectorProps = {
  /** Currently selected language */
  selectedLocale: LocaleType;
  /** Callback function when language selection changes */
  onLocaleChange: (locale: LocaleType) => void;
  /** Whether the selector is disabled */
  disabled?: boolean;
  /** Additional className for styling */
  className?: string;
};

/**
 * Language display names in English and their native names with associated country codes for flags
 */
const LANGUAGE_NAMES: Record<LocaleType, { 
  english: string; 
  native: string;
  countryCode: string; 
}> = {
  ar: { english: 'Arabic', native: 'العربية', countryCode: 'SA' }, // Saudi Arabia
  en: { english: 'English', native: 'English', countryCode: 'US' }, // United States
  zh: { english: 'Chinese', native: '中文', countryCode: 'CN' }, // China
  ja: { english: 'Japanese', native: '日本語', countryCode: 'JP' }, // Japan
  ru: { english: 'Russian', native: 'Русский', countryCode: 'RU' }, // Russia
  de: { english: 'German', native: 'Deutsch', countryCode: 'DE' }, // Germany
  fr: { english: 'French', native: 'Français', countryCode: 'FR' }, // France
  es: { english: 'Spanish', native: 'Español', countryCode: 'ES' }, // Spain
  hi: { english: 'Hindi', native: 'हिन्दी', countryCode: 'IN' }, // India
  tr: { english: 'Turkish', native: 'Türkçe', countryCode: 'TR' }, // Turkey
  sv: { english: 'Swedish', native: 'Svenska', countryCode: 'SE' }  // Sweden
};

/**
 * Language selector component
 * @param {LanguageSelectorProps} props - Component props
 * @returns {JSX.Element} Rendered component
 */
export function LanguageSelector({ 
  selectedLocale, 
  onLocaleChange, 
  disabled = false,
  className = ""
}: LanguageSelectorProps) {
  const [open, setOpen] = useState(false);
  const { triggerHaptic } = useHapticFeedback();
  const isMobile = useIsMobile();
  const { isRTL } = useLanguage(); // Get RTL status from context
  const { t } = useAppTranslation(); // Get translation function

  /**
   * Handles language selection change with haptic feedback
   * @param {LocaleType} locale - Selected language code
   */
  const handleLocaleChange = useCallback(async (locale: LocaleType) => {
    if (locale !== selectedLocale) {
      await triggerHaptic('selection');
      // Just call the parent's onLocaleChange - don't activate again here
      // This avoids duplicate locale activations
      onLocaleChange(locale);
      setOpen(false);
    }
  }, [selectedLocale, onLocaleChange, triggerHaptic]);

  /**
   * Gets display name for language based on its code
   * @param {LocaleType} locale - Language code
   * @returns {string} Display name (native only)
   */
  const getLanguageDisplayName = (locale: LocaleType) => {
    return LANGUAGE_NAMES[locale].native;
  };

  return (
    <DropdownMenu open={open} onOpenChange={setOpen}>
      <DropdownMenuTrigger asChild disabled={disabled}>
        <Button 
          variant="outline"
          className={cn(
            "model-selector-btn h-10 transform-gpu will-change-transform flex items-center gap-2",
            className
          )}
          style={{ contain: "layout" }}
          aria-label={t("Select language")}
        >
          <ReactCountryFlag
            countryCode={LANGUAGE_NAMES[selectedLocale].countryCode}
            svg
            cdnUrl="https://cdnjs.cloudflare.com/ajax/libs/flag-icon-css/3.5.0/flags/4x3/"
            cdnSuffix="svg"
            style={{
              width: '1rem',
              height: '1rem',
              borderRadius: '0.125rem',
            }}
            title={LANGUAGE_NAMES[selectedLocale].english}
            aria-hidden="true"
          />
          <span className="ml-2 hidden md:inline whitespace-nowrap font-semibold">
            {getLanguageDisplayName(selectedLocale)}
          </span>
          <span className="ml-2 md:hidden whitespace-nowrap font-semibold">
            {LANGUAGE_NAMES[selectedLocale].native}
          </span>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        side="top"
        align="end"
        className="min-w-[180px] transform-gpu will-change-transform"
        style={{
          contain: "layout style paint",
          transition: "transform 150ms ease, opacity 150ms ease",
          WebkitOverflowScrolling: 'touch', // Smooth scrolling on iOS
          msOverflowStyle: 'none',  // Hide scrollbar in Edge
          scrollbarWidth: 'none',   // Hide scrollbar in Firefox
        }}
      >
        {Object.keys(LANGUAGE_NAMES).map((locale) => {
          const localeType = locale as LocaleType;
          return (
            <DropdownMenuItem
              key={locale}
              className="flex items-center justify-between gap-2 px-3 py-2 transform-gpu"
              onClick={() => handleLocaleChange(localeType)}
              aria-checked={selectedLocale === locale}
            >
              <div className="flex items-center gap-2">
                <ReactCountryFlag
                  countryCode={LANGUAGE_NAMES[localeType].countryCode}
                  svg
                  cdnUrl="https://cdnjs.cloudflare.com/ajax/libs/flag-icon-css/3.5.0/flags/4x3/"
                  cdnSuffix="svg"
                  style={{
                    width: '1rem',
                    height: '1rem',
                    borderRadius: '0.125rem',
                  }}
                  title={LANGUAGE_NAMES[localeType].english}
                  aria-hidden="true"
                />
                <span className="font-semibold">{getLanguageDisplayName(localeType)}</span>
              </div>
              {selectedLocale === locale && <Check className="h-4 w-4 flex-shrink-0" aria-hidden="true" />}
            </DropdownMenuItem>
          );
        })}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}